/**
 * Frame Thumbnail Strip — Decorivie / Combine adaptation
 *
 * Listens to Combine's <product-variants> element (VARIANT_CHANGE event) to
 * detect Type option changes (Only Print / Framed), filters the thumbnail grid
 * accordingly, and:
 *   - Highlights the selected thumb
 *   - Swaps the main gallery image (best-effort, without touching gallery state)
 *   - Writes the "Frame Color & Type" line-item property (visible in cart)
 *
 * Multiple instances safe — scopes everything to its own [data-fts-root].
 */
(function () {
  'use strict';

  if (window.__ftsBooted) return;
  window.__ftsBooted = true;

  var DEFAULT_FRAMED_LABEL = 'Black'; // preferred default when Type=Framed
  var GALLERY_ITEM_SELECTOR = '.product-gallery-item [data-media-id]';
  var GALLERY_IMG_SELECTOR = '.product-gallery-item img';

  function each(nodes, fn) { Array.prototype.forEach.call(nodes || [], fn); }

  function findProductVariants(root) {
    // Look for the Combine <product-variants> element within the same section container
    var section = root.closest('.shopify-section') || document;
    return section.querySelector('product-variants[data-main-product-variants]')
        || section.querySelector('product-variants')
        || document.querySelector('product-variants[data-main-product-variants]');
  }

  function readCurrentType(root, productVariants) {
    var typeIndex = parseInt(root.getAttribute('data-type-option-index') || '0', 10);
    if (productVariants && productVariants.currentVariant && typeof productVariants.currentVariant === 'object') {
      var key = 'option' + (typeIndex + 1);
      var val = productVariants.currentVariant[key];
      if (val) return val;
    }
    // Fallback: look for selected option in DOM
    var selected = document.querySelector('[data-option-value-id][data-selected]');
    if (selected) return selected.textContent.trim();
    return '';
  }

  function classifyType(typeString) {
    if (!typeString) return 'framed';
    return (typeString.trim() === 'Only Print') ? 'onlyprint' : 'framed';
  }

  function firstVisibleOfType(root, group) {
    var buttons = root.querySelectorAll('[data-fts-thumb]');
    var preferred = null;
    var fallback = null;
    for (var i = 0; i < buttons.length; i++) {
      var b = buttons[i];
      if (b.getAttribute('data-type') !== group) continue;
      if (!fallback) fallback = b;
      if (group === 'framed' && b.getAttribute('data-frame') === DEFAULT_FRAMED_LABEL) {
        preferred = b;
        break;
      }
    }
    return preferred || fallback;
  }

  function getPropInput(root) {
    // Look inside product-form first (post-move), then fall back to section root.
    var pf = findProductFormElement();
    return (pf && pf.querySelector('[data-fts-prop]'))
        || root.querySelector('[data-fts-prop]')
        || document.querySelector('[data-fts-prop]');
  }

  function setActive(root, btn) {
    if (!btn) return;
    each(root.querySelectorAll('[data-fts-thumb]'), function (b) {
      b.classList.remove('is-active');
      b.setAttribute('aria-pressed', 'false');
    });
    btn.classList.add('is-active');
    btn.setAttribute('aria-pressed', 'true');

    var frame = btn.getAttribute('data-frame') || '';
    var prop = getPropInput(root);
    if (prop) prop.value = frame;
  }

  function swapMainImage(root, btn) {
    var src = btn.getAttribute('data-src');
    if (!src) return;

    // Combine gallery: img inside .product-gallery-item. Swap the first slide's img(s).
    var galleryImgs = document.querySelectorAll('.product-gallery [data-index="0"] img, .product-gallery-item[data-index="0"] img');
    if (!galleryImgs.length) {
      // Fallback selector for lazy-image wrappers
      galleryImgs = document.querySelectorAll('.product-gallery-item img');
      galleryImgs = galleryImgs.length ? [galleryImgs[0]] : [];
    }

    each(galleryImgs, function (img) {
      if (!img) return;
      // Blur while loading
      var prevFilter = img.style.filter;
      img.style.transition = 'filter .35s ease';
      img.style.filter = 'blur(10px)';
      // Remove responsive attrs so the new src wins
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
      img.src = src;
      var clear = function () {
        img.style.filter = prevFilter || '';
        img.removeEventListener('load', clear);
      };
      img.addEventListener('load', clear);
      setTimeout(clear, 900); // safety
    });
  }

  function syncByType(root, typeGroup, allowImageSwap) {
    var heading = root.querySelector('[data-fts-heading]');
    if (heading) {
      heading.textContent = (typeGroup === 'onlyprint')
        ? (root.getAttribute('data-heading-onlyprint') || 'Print Type')
        : (root.getAttribute('data-heading-framed') || 'Frame Color & Type');
    }
    var firstVisible = null;
    each(root.querySelectorAll('[data-fts-thumb]'), function (b) {
      var match = b.getAttribute('data-type') === typeGroup;
      b.hidden = !match;
      if (match && !firstVisible) firstVisible = b;
    });
    var active = root.querySelector('[data-fts-thumb].is-active');
    if (!active || active.hidden) {
      var chosen = firstVisibleOfType(root, typeGroup) || firstVisible;
      if (chosen) {
        setActive(root, chosen);
        if (allowImageSwap) swapMainImage(root, chosen);
      }
    }
  }

  function findProductForm() {
    // Combine builds product-form IDs like "product-form-template--25763006218532__main".
    var f = document.querySelector('form[id^="product-form-"][id$="__main"]')
         || document.querySelector('form[id^="product-form-"]:not([id="product-form-installment"])');
    return f;
  }

  function findProductFormElement() {
    // Combine wraps the form in a <product-form> custom element and its FormData
    // collection uses `this.querySelectorAll('[name*="properties"]')` — meaning
    // our hidden input MUST live INSIDE the <product-form> subtree to be picked
    // up on Add to Cart. HTML5 `form=` attr does not help here.
    return document.querySelector('product-form[data-js-product-form]')
        || document.querySelector('product-form');
  }

  function attachPropInputToForm(root) {
    var propInSection = root.querySelector('[data-fts-prop]');
    var pf = findProductFormElement();
    if (!propInSection || !pf) return null;

    // If we already moved it, return the existing one inside product-form
    var existing = pf.querySelector('[data-fts-prop]');
    if (existing) {
      // Sync value from the (now-removed) section input if any leftover
      if (propInSection !== existing && propInSection.value && !existing.value) {
        existing.value = propInSection.value;
      }
      if (propInSection !== existing) propInSection.remove();
      return existing;
    }

    // Move (not clone) the input into the product-form subtree so Combine's
    // JS FormData collector finds it via `this.querySelectorAll([name*="properties"])`.
    var form = findProductForm();
    if (form) {
      form.appendChild(propInSection);
    } else {
      pf.appendChild(propInSection);
    }
    return propInSection;
  }

  function init(root) {
    if (root.__ftsInitialized) return;
    root.__ftsInitialized = true;

    var pv = findProductVariants(root);
    attachPropInputToForm(root);

    // Click handlers
    each(root.querySelectorAll('[data-fts-thumb]'), function (b) {
      b.setAttribute('aria-pressed', 'false');
      b.addEventListener('click', function () {
        setActive(root, b);
        swapMainImage(root, b);
      });
    });

    // Initial sync — do NOT swap image on first paint (respect gallery's own first image)
    var initialType = classifyType(readCurrentType(root, pv));
    syncByType(root, initialType, false);

    // Listen for VARIANT_CHANGE from Combine's <product-variants>
    if (pv) {
      pv.addEventListener('VARIANT_CHANGE', function () {
        var newType = classifyType(readCurrentType(root, pv));
        syncByType(root, newType, false);
      });
    }

    // Also listen for global Krown bus fallback
    if (window.KROWN && window.KROWN.events && typeof window.KROWN.events.subscribe === 'function') {
      window.KROWN.events.subscribe('krown:variant:change', function () {
        var newType = classifyType(readCurrentType(root, pv));
        syncByType(root, newType, false);
      });
    } else {
      document.addEventListener('krown:variant:change', function () {
        var newType = classifyType(readCurrentType(root, pv));
        syncByType(root, newType, false);
      });
    }
  }

  function bootAll() {
    each(document.querySelectorAll('[data-fts-root]'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }
})();
